import React, { forwardRef, memo, SVGAttributes } from 'react';
import classnames from 'classnames';
import { motion } from 'framer-motion';
import { definedAccessorXY, getOrigin, noTransition } from './utils';
import { SeriesPointXY } from './Types';
import VizContext from './VizContext';

const { useContext } = React;

export interface Props extends SVGAttributes<SVGGElement> {
  /**
   * Flag for enabling animations.
   */
  animate?: boolean;
  /**
   * Flag to clip the path to the artboard area.
   */
  clipToArtboard?: boolean;
  /**
   * Sets the origin x value, from where each point will animate from
   * on the x axis. This can be a single value which will be used for
   * all points, or a function that given the point returns a number.
   */
  originX?: number | ((point: SeriesPointXY) => number);
  /**
   * Sets the origin y value, from where each point will animate from
   * on the y axis. This can be a single value which will be used for
   * all points, or a function that given the point returns a number.
   */
  originY?: number | ((point: SeriesPointXY) => number);
  /**
   * Series of data point objects, with X and Y values that lie within
   * the given domains.
   */
  series: SeriesPointXY[];
  /**
   * Radius of the circles
   *
   * @default 4
   */
  r?: number;
}

/**
 * Circle series component, that draws a circle for each data points given within
 * the series.
 */
export const VizSVGCircles = forwardRef<SVGGElement, Props>((props: Props, ref) => {
  const {
    animate,
    className,
    clipToArtboard,
    opacity = 1,
    originX,
    originY,
    r = 4,
    series,
    ...rest
  } = props;

  const {
    defArtboardClipMaskId,
    getX,
    getY,
    transition,
  } = useContext(VizContext);

  return (
    <g { ...rest }
        className={ classnames('Bitviz__series-circles', className) }
        clipPath={ clipToArtboard ? `url(#${defArtboardClipMaskId})` : undefined }
        ref={ ref }>
      { series
        .filter(definedAccessorXY)
        .map((point: SeriesPointXY, index) => (
          <motion.circle
              animate={ {
                opacity: opacity,
                r: r,
                x: getX(point.x) as number,
                y: getY(point.y) as number,
              } }
              initial={ {
                opacity: 0,
                r: 0,
                x: getX(getOrigin(point, point.x, originX)) as number,
                y: getY(getOrigin(point, point.y, originY)) as number,
              } }
              key={ index }
              transition={ animate ? transition : noTransition } />
        )) }
    </g>
  );
});

export default memo(VizSVGCircles);
